import { gql } from '@apollo/client';
import Avatar, { type AvatarShape, type IAvatarProps } from '@orn/shared-web/components/Avatar';
import { bankLogoSrc } from '@orn/shared-web/utils/bankLogoSrc';
import { getInitial } from '@orn/shared-web/utils/person/getInitials';

import { MANTLE_MARK_TILE_SRC } from '../../logos/mantleLogo';

import type { EntityAvatarFragment } from './__generated__/EntityAvatar.gql';

interface IEntityAvatarProps extends Omit<IAvatarProps, 'src' | 'contrastBorder' | 'initials' | 'shape'> {
  entity: EntityAvatarFragment;
}

const EntityAvatar = (props: IEntityAvatarProps) => {
  const { entity, ...avatarProps } = props;
  const { src, shape, contrastBorder, initial } = getAvatarProps(entity);

  return <Avatar {...avatarProps} src={src} shape={shape} contrastBorder={contrastBorder} initials={initial} />;
};

const EntityAvatarFragment = gql`
  fragment EntityAvatar on IEntity {
    id
    name

    ... on User {
      avatarUrl
    }
    ... on Bank {
      logoBase64Encoding
      logoUrl
    }
    ... on Merchant {
      logoUrl
      isMantle
    }

    ... on FinancialAccount {
      bank {
        id
        logoBase64Encoding
        logoUrl
      }
    }
  }
`;

export default Object.assign(EntityAvatar, { Fragment: EntityAvatarFragment });

/**
 * Where this entity's logo comes from, or `null` when it has none.
 *
 * Exported because the assistant's entity chip draws the same logo with its own
 * treatment — a type glyph rather than an initial where there is none — and it
 * has to resolve the same field per type to do it.
 *
 * Normalizes an empty string to `null`, so a caller can ask `=== null` rather
 * than re-deriving falsiness. The household's own Mantle merchant, which carries no logo,
 * draws the Mantle mark.
 */
export const entityLogoSrc = (entity: EntityAvatarFragment): string | null => {
  switch (entity.__typename) {
    case 'User':
      return entity.avatarUrl || null;
    case 'Merchant':
      return entity.logoUrl || (entity.isMantle ? MANTLE_MARK_TILE_SRC : null);
    case 'FinancialAccount':
      return bankLogoSrc(entity.bank);
    case 'Bank':
      return bankLogoSrc(entity);
    default:
      return null;
  }
};

const getAvatarProps = (entity: EntityAvatarFragment) => {
  const src = entityLogoSrc(entity);
  const isSquare = entity.__typename === 'FinancialAccount' || entity.__typename === 'Bank';

  return {
    src,
    // oxlint-disable-next-line typescript/no-unnecessary-type-assertion -- without it the ternary widens to string; tsgolint misreads the cast as redundant
    shape: (isSquare ? 'square' : 'circle') as AvatarShape,
    // The contrast border marks the absence of a logo, so it asks the same
    // question `src` answered rather than re-deriving it from the same fields.
    contrastBorder: src === null,
    initial: getInitial(entity.name),
  };
};
